/**
 * Hats that grow with the wearer: the Pub Coin Top Hat gets taller the more
 * coin you hold, the Pint Tower stacks another pint on your head for every
 * week of your streak, the Pub Beard grows longer with every hour you've
 * ever spent in the pub on a Thursday, and the Movember Moustache grows wider
 * with this month's Thursday hours, then gets shaved off on the 1st. There's
 * no cap, so a rich or loyal bro's hat can run right off the screen.
 *
 * Their hat value is a token rather than art, like the trophy crowns, so the
 * size can be worked out from whoever's wearing it each time it's drawn.
 */

export const COIN_TOP_HAT = "dynamic-hat:coin";
export const STREAK_TOP_HAT = "dynamic-hat:streak";
export const PUB_BEARD = "dynamic-hat:beard";
export const MONTHLY_MOUSTACHE = "dynamic-hat:moustache";

export type DynamicHatKind = "coin" | "streak" | "beard" | "moustache";

const KINDS_BY_VALUE: Record<string, DynamicHatKind> = {
  [COIN_TOP_HAT]: "coin",
  [STREAK_TOP_HAT]: "streak",
  [PUB_BEARD]: "beard",
  [MONTHLY_MOUSTACHE]: "moustache",
};

/**
 * Pixels of growth per unit, on a standard 40px avatar with a 25px hat:
 * per coin, per week of streak, and per hour of Thursday time (the beard's
 * length, and the moustache's reach on each side).
 */
const PX_PER_UNIT: Record<DynamicHatKind, number> = {
  coin: 1 / 4,
  streak: 5,
  beard: 1 / 10,
  moustache: 1,
};

/** The font size, in px, a hat is drawn at on a standard avatar. */
export const STANDARD_HAT_PX = 25;

/** Which dynamic hat a hat value is, or null for an ordinary hat. */
export function parseDynamicHat(value: string): DynamicHatKind | null {
  return KINDS_BY_VALUE[value] ?? null;
}

/**
 * How much bigger than its base size the hat is, in px on a standard
 * avatar: a pixel per four coin, five per week of streak, a pixel per ten
 * hours for the beard and one per hour for the moustache. Debt doesn't
 * shrink it below its base size.
 */
export function getDynamicHatGrowth(kind: DynamicHatKind, amount: number) {
  return Math.max(0, Math.floor(amount)) * PX_PER_UNIT[kind];
}

/**
 * How many pints are in the Pint Tower: one to start, then one more for
 * each week of streak. Each pint is PINT_STEP_PX tall, so the tower grows
 * at the streak's rate.
 */
export function getPintCount(streak: number) {
  return Math.max(0, Math.floor(streak)) + 1;
}

/** How tall each pint in the Pint Tower is, in px on a standard avatar. */
export const PINT_STEP_PX = PX_PER_UNIT.streak;
